/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-tagless-components, no-unused-vars */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { scheduleOnce, later } from '@ember/runloop';
import { classNames, tagName } from '@ember-decorators/component';

@tagName('div')
@classNames('poster-container')
export default class TopicPrinter extends Component {
  @service
  toaster;

  // Track retry attempts for QR code extraction
  qrExtractionAttempts = 0;

  maxQrExtractionAttempts = 5;
  qrExtractionDelay = 200; // ms

  // Iframe reference for cleanup
  _iframe = null;

  @tracked
  qrImage;

  // Compute full HTML content
  @computed('topic', 'qrImage')
  get posterHtml() {
    let topic = this.topic;
    let qrImage = this.qrImage; // Extracted QR code image

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Printable Poster</title>
          <style>
               body {
                  font-family: Arial, sans-serif;
                  margin: 20px;
                  padding: 20px;
                  background: #f8f8f8;
                  color: #333;
              }
              .poster {
                  background: white;
                  padding: 30px;
                  max-width: 800px;
                  margin: auto;
              }
              .header {
                  display: flex;
                  align-items: center;
                  gap: 20px;
              }
              .qr-code-container {
                  flex-shrink: 0;
              }
              .qr-code-container img {
                  width: 150px;
              }
              h1 {
                  color: #004080;
                  margin: 0;
                  flex-grow: 1;
              }
              h2.keyQuestions {
                background-color: #efa6a9;
              }
              h2.learningIntentions {
                background-color: #d2eded;
              }
              h2.successCriteria {
                background-color: #ffcd9f;
              }
              h2.learningConnections {
                background-color: #bbdcf8;
              }
              h2.assessmentTasks {
                background-color: #fec3d5;
              }
              h2.teachingStrategies {
                background-color: #C2E09E;
              }
              .section { margin-bottom: 20px; }
              .section h2 { color: black; padding: 10px; border-radius: 15px; }
              .qr-code-container { margin: 20px auto; text-align: center; }
              .qr-code-container img { width: 150px;}
          </style>
        </head>
        <body>
          <div class="poster">
            <div class="header">
              ${
                qrImage
                  ? `<div class="qr-code-container"><img src="${qrImage}" alt="QR Code"></div>`
                  : ''
              }
              ${
                topic.educatorDescription
                  ? `<h1>${topic.educatorDescription}</h1>`
                  : ''
              }
            </div>
            ${
              topic.learningIntentions
                ? `<div class="section"><h2 class="learningIntentions">Learning Intentions</h2>${topic.learningIntentions}</div>`
                : ''
            }
            ${
              topic.successCriteria
                ? `<div class="section"><h2 class="successCriteria">Success Criteria</h2>${topic.successCriteria}</div>`
                : ''
            }
            ${
              topic.keyQuestions
                ? `<div class="section"><h2 class="keyQuestions">Key Questions</h2>${topic.keyQuestions}</div>`
                : ''
            }
            ${
              topic.learningConnections
                ? `<div class="section"><h2 class="learningConnections">Connections</h2>${topic.learningConnections}</div>`
                : ''
            }
          </div>
        </body>
      </html>
    `;
  }

  didInsertElement() {
    super.didInsertElement(...arguments);

    // Extract QR code as an image with retry mechanism
    scheduleOnce('afterRender', this, this.attemptQrExtraction);
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);

    // Clean up iframe references to prevent memory leaks
    if (this._iframe) {
      try {
        // Clean up event listeners or other references if needed
        const iframe = this._iframe;
        if (iframe.contentWindow) {
          iframe.contentWindow.onload = null;
        }
        this._iframe = null;
      } catch (_) {
        // Ignore error
      }
    }
  }

  attemptQrExtraction() {
    if (this.qrExtractionAttempts >= this.maxQrExtractionAttempts) {
      // Load iframe content anyway without QR code
      this.loadIframeContent();
      return;
    }

    this.extractQRCode();

    // If no QR code found, retry after a delay
    if (!this.qrImage) {
      this.incrementProperty('qrExtractionAttempts');
      later(this, this.attemptQrExtraction, this.qrExtractionDelay);
    }
  }

  extractQRCode() {
    try {
      let qrCanvas = document.querySelector('.qr-code-container canvas'); // Find the QR code canvas
      if (qrCanvas) {
        let qrImage = qrCanvas.toDataURL('image/png'); // Convert canvas to base64 image
        this.qrImage = qrImage; // Store it in a property

        // Now inject the updated content with QR code into the iframe
        this.loadIframeContent();
      }
    } catch (_) {
      this.toaster.error('Error extracting QR code');
    }
  }

  loadIframeContent() {
    try {
      let iframe = this.element.querySelector('iframe');
      if (iframe) {
        // Store reference for cleanup
        this._iframe = iframe;

        let doc = iframe.contentWindow.document;
        doc.open();
        doc.write(this.posterHtml);
        doc.close();
      }
    } catch (_) {
      this.paperToaster.show(`'Error loading iFrame:`, {
        duration: 4000,
        position: 'top right',
        toastClass: 'warning',
      });
    }
  }

  @action
  printPoster() {
    try {
      let iframe = this.element.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }
    } catch (_) {
      this.toaster.error('An error occurred while printing.');
    }
  }
}
